import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Suppliers from "./pages/Suppliers";
import Companies from "./pages/Companies";
import PurchaseOrders from "./pages/PurchaseOrders";
import Invoices from "./pages/Invoices";
import PaymentRequests from "./pages/PaymentRequests";
import Users from "./pages/Users";
import InternalPaymentForm from "./pages/InternalPaymentForm";
import OperasionalPaymentForm from "./pages/OperasionalPaymentForm";
import Recipients from "./pages/Recipients";
import BankProcess from "./pages/BankProcess";
import BankCodes from "./pages/BankCodes";
import RequestTypes from "./pages/RequestTypes";
import VirtualAccounts from "./pages/VirtualAccounts";
import UploadBukti from "./pages/UploadBukti";
import Products from "./pages/Products";
import AuditLog from "./pages/AuditLog";
import Departments from "./pages/Departments";
import { Toaster } from "sonner";
import { ForceChangePassword } from "./components/ForceChangePassword";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }

  if (user.must_change_password) {
    return <ForceChangePassword />;
  }
  
  // Internal & operasional users are redirected to their dedicated form
  if (FORM_ROLE_HOME[user.role]) {
    return <Navigate to={FORM_ROLE_HOME[user.role]} />;
  }
  
  return children;
}

// Peran yang hanya memakai satu form pengajuan, beserta halamannya.
const FORM_ROLE_HOME = {
  internal: '/internal-payment',
  operasional: '/operasional-payment',
};

function FormRoleRoute({ role, children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  // Hanya peran pemilik form yang boleh membuka halaman ini; peran form lain
  // dikembalikan ke form miliknya sendiri.
  if (user.role !== role) {
    return <Navigate to={FORM_ROLE_HOME[user.role] || '/'} />;
  }
  
  return children;
}

// Halaman khusus admin (server juga menolak non-admin dengan 403).
function AdminRoute({ children }) {
  const { user } = useAuth();
  if (user?.role !== 'admin') return <Navigate to="/" />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/internal-payment" element={
            <FormRoleRoute role="internal">
              <InternalPaymentForm />
            </FormRoleRoute>
          } />
          <Route path="/operasional-payment" element={
            <FormRoleRoute role="operasional">
              <OperasionalPaymentForm />
            </FormRoleRoute>
          } />
          <Route path="/" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/suppliers" element={
            <ProtectedRoute>
              <Suppliers />
            </ProtectedRoute>
          } />
          <Route path="/companies" element={
            <ProtectedRoute>
              <Companies />
            </ProtectedRoute>
          } />
          <Route path="/purchase-orders" element={
            <ProtectedRoute>
              <PurchaseOrders />
            </ProtectedRoute>
          } />
          <Route path="/invoices" element={
            <ProtectedRoute>
              <Invoices />
            </ProtectedRoute>
          } />
          <Route path="/payment-requests" element={
            <ProtectedRoute>
              <PaymentRequests />
            </ProtectedRoute>
          } />
          <Route path="/recipients" element={
            <ProtectedRoute>
              <Recipients />
            </ProtectedRoute>
          } />
          <Route path="/bank-process" element={
            <ProtectedRoute>
              <BankProcess />
            </ProtectedRoute>
          } />
          <Route path="/bank-codes" element={
            <ProtectedRoute>
              <BankCodes />
            </ProtectedRoute>
          } />
          <Route path="/request-types" element={
            <ProtectedRoute>
              <RequestTypes />
            </ProtectedRoute>
          } />
          <Route path="/virtual-accounts" element={
            <ProtectedRoute>
              <VirtualAccounts />
            </ProtectedRoute>
          } />
          <Route path="/upload-bukti" element={
            <ProtectedRoute>
              <UploadBukti />
            </ProtectedRoute>
          } />
          <Route path="/users" element={
            <ProtectedRoute>
              <Users />
            </ProtectedRoute>
          } />
          <Route path="/audit-log" element={
            <ProtectedRoute>
              <AdminRoute><AuditLog /></AdminRoute>
            </ProtectedRoute>
          } />
            <Route path="/departments" element={
            <ProtectedRoute>
              <AdminRoute><Departments /></AdminRoute>
            </ProtectedRoute>
          } />
          <Route path="/products" element={
            <ProtectedRoute>
              <Products />
            </ProtectedRoute>
          } />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors duration={2000} />
    </AuthProvider>
  );
}

export default App;

